import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Building2, 
  Plus,
  LayoutGrid,
  List,
  Search,
  Download,
  Upload
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useToastHelpers } from '@/contexts/ToastContext'
import { apiClient, PropertyResponse } from '@/services/api'
import { PortfolioAnalytics, AnalysisCoverageData } from '@/types/portfolio'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { NavigationHeader } from '@/components/ui/NavigationHeader'
import { PropertyCard } from '@/components/ui/PropertyCard'
import { PortfolioStats } from '@/components/ui/PortfolioStats'
import { AnalysisCoverageChart } from '@/components/ui/AnalysisCoverageChart'
import { PropertyCardSkeleton, CompactPropertyCardSkeleton } from '@/components/ui/Skeleton'
import { PropertyComparison } from '@/components/ui/PropertyComparison'
import { PropertyImportModal } from '@/components/ui/PropertyImportModal'
import { generatePortfolioCSV, downloadCSV, generateFilename } from '@/utils/export'
import { calculateMonthlyPayment } from '@/utils/propertyCalculations'

export function PortfolioPage() {
  const { user } = useAuth()
  const { showSuccess, showError } = useToastHelpers()
  const navigate = useNavigate()
  const [properties, setProperties] = useState<PropertyResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortingError, setSortingError] = useState<string | null>(null)
  const [portfolioAnalytics, setPortfolioAnalytics] = useState<PortfolioAnalytics | null>(null)
  const [coverageData, setCoverageData] = useState<AnalysisCoverageData | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'buy-hold' | 'flip' | 'brrr'>('all')
  const [sortBy, setSortBy] = useState<'date' | 'price' | 'cashflow' | 'roi'>('date')
  const [_showFilters, _setShowFilters] = useState(false)
  const [selectedProperties, setSelectedProperties] = useState<string[]>([])
  const [showComparison, setShowComparison] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)

  useEffect(() => {
    loadProperties()
  }, [])

  const loadProperties = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load properties and analytics in parallel
      const [propertiesResponse, analyticsResponse, coverageResponse] = await Promise.allSettled([
        apiClient.getUserProperties({ limit: 50 }),
        apiClient.getPortfolioAnalytics(),
        apiClient.getAnalysisCoverage()
      ])

      // Handle properties response
      if (propertiesResponse.status === 'fulfilled') {
        setProperties(propertiesResponse.value.properties)
      } else {
        throw propertiesResponse.reason
      }

      // Handle analytics response (optional)
      if (analyticsResponse.status === 'fulfilled' && analyticsResponse.value.success) {
        setPortfolioAnalytics(analyticsResponse.value.data)
      } else {
        console.warn('Portfolio analytics failed to load:', analyticsResponse.status === 'rejected' ? analyticsResponse.reason : 'Unknown error')
      }

      // Handle coverage response (optional)
      if (coverageResponse.status === 'fulfilled' && coverageResponse.value.success) {
        setCoverageData(coverageResponse.value.data)
      } else {
        console.warn('Analysis coverage failed to load:', coverageResponse.status === 'rejected' ? coverageResponse.reason : 'Unknown error')
      }

    } catch (err) {
      console.error('Failed to load properties:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load properties'
      setError(errorMessage)
      showError('Failed to Load Portfolio', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteProperty = async (property: PropertyResponse) => {
    if (!confirm(`Are you sure you want to delete ${property.property_address}?`)) {
      return
    }

    try {
      await apiClient.deleteUserProperty(property.property_id)
      setProperties(prev => prev.filter(p => p.property_id !== property.property_id))
      showSuccess('Property Deleted', `${property.property_address} has been removed from your portfolio.`)
    } catch (err) {
      console.error('Failed to delete property:', err)
      showError('Delete Failed', 'Failed to delete property. Please try again.')
    }
  }

  const handleViewProperty = (property: PropertyResponse) => {
    navigate(`/property/${property.property_id}`)
  }

  const handleEditProperty = (property: PropertyResponse) => {
    navigate(`/property/${property.property_id}/edit`)
  }

  const togglePropertySelection = (propertyId: string) => {
    setSelectedProperties(prev => 
      prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    )
  }

  const handleCompareProperties = () => {
    if (selectedProperties.length < 2) {
      showError('Selection Required', 'Please select at least 2 properties to compare.')
      return
    }
    setShowComparison(true)
  }

  const handleRemoveFromComparison = (propertyId: string) => {
    setSelectedProperties(prev => prev.filter(id => id !== propertyId))
    if (selectedProperties.length <= 2) {
      setShowComparison(false)
    }
  }

  const handleExportPortfolio = () => {
    try {
      if (properties.length === 0) {
        showError('Export Failed', 'No properties available to export.')
        return
      }

      const csvContent = generatePortfolioCSV(properties)
      const filename = generateFilename('real_estate_portfolio', 'csv')
      
      downloadCSV(csvContent, filename)
      showSuccess('Export Successful', `Portfolio exported as ${filename}`)
    } catch (error) {
      console.error('Export failed:', error)
      showError('Export Failed', 'Failed to export portfolio. Please try again.')
    }
  }

  const handleImportComplete = (newProperties: PropertyResponse[]) => {
    setProperties(prev => [...prev, ...newProperties])
    setShowImportModal(false)
  }

  const selectedPropertiesData = properties.filter(p => selectedProperties.includes(p.property_id))

  // Helper functions for calculations (declared as regular functions to avoid initialization issues)
  const calculateCashFlow = (property: PropertyResponse): number => {
    try {
      // Validate required fields with safe defaults
      const loanAmount = Number(property.loan_amount) || 0
      const interestRate = Number(property.interest_rate) || 0
      const loanTerm = Number(property.loan_term) || 30

      // Calculate monthly mortgage payment safely
      const monthlyMortgage = loanAmount > 0 ? calculateMonthlyPayment(
        loanAmount,
        interestRate,
        loanTerm
      ) : 0

      // Safe calculations with fallback values, ensuring all values are numbers
      const annualTaxes = Number(property.annual_taxes) || 0
      const annualInsurance = Number(property.annual_insurance) || 0
      const monthlyHOA = Number(property.monthly_hoa_fees) || 0
      const expectedRent = Number(property.expected_monthly_rent) || 0

      // Calculate monthly expenses
      const monthlyTaxes = annualTaxes / 12
      const monthlyInsurance = annualInsurance / 12

      // Validate intermediate calculations
      const validMonthlyMortgage = isFinite(monthlyMortgage) ? monthlyMortgage : 0
      const validMonthlyTaxes = isFinite(monthlyTaxes) ? monthlyTaxes : 0
      const validMonthlyInsurance = isFinite(monthlyInsurance) ? monthlyInsurance : 0
      const validMonthlyHOA = isFinite(monthlyHOA) ? monthlyHOA : 0
      const validExpectedRent = isFinite(expectedRent) ? expectedRent : 0

      const monthlyExpenses = validMonthlyMortgage + validMonthlyTaxes + validMonthlyInsurance + validMonthlyHOA
      const cashFlow = validExpectedRent - monthlyExpenses

      // Return valid number or 0 if calculation failed
      return isFinite(cashFlow) ? cashFlow : 0
    } catch (error) {
      console.warn('Cash flow calculation error for property:', property.property_id, error)
      return 0
    }
  }

  const calculateROI = (property: PropertyResponse): number => {
    try {
      const monthlyCashFlow = calculateCashFlow(property)
      const downPayment = Number(property.down_payment) || 0

      // Validate monthly cash flow calculation
      const validMonthlyCashFlow = isFinite(monthlyCashFlow) ? monthlyCashFlow : 0

      // Handle zero or missing down payment
      if (downPayment <= 0) {
        return 0
      }

      // Calculate annual cash flow
      const annualCashFlow = validMonthlyCashFlow * 12

      // Validate annual cash flow
      if (!isFinite(annualCashFlow)) {
        return 0
      }

      // Calculate ROI as percentage
      const roi = (annualCashFlow / downPayment) * 100

      // Return valid ROI or 0 if calculation failed
      return isFinite(roi) ? Number(roi.toFixed(2)) : 0
    } catch (error) {
      console.warn('ROI calculation error for property:', property.property_id, error)
      return 0
    }
  }

  // Filter and sort properties
  const filteredAndSortedProperties = React.useMemo(() => {
    try {
      setSortingError(null) // Clear any previous sorting errors
      let filtered = properties

      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(property =>
          property.property_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
          property.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          property.state?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }

      // Apply analysis type filter
      if (filterType !== 'all') {
        filtered = filtered.filter(property => property.analysis_status === filterType)
      }

      // Apply sorting with comprehensive error handling
      try {
        filtered = [...filtered].sort((a, b) => {
          try {
            switch (sortBy) {
              case 'date': {
                const dateA = new Date(a.created_at)
                const dateB = new Date(b.created_at)

                // Handle invalid dates
                if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0
                if (isNaN(dateA.getTime())) return 1
                if (isNaN(dateB.getTime())) return -1

                return dateB.getTime() - dateA.getTime()
              }
              case 'price': {
                const priceA = a.purchase_price || 0
                const priceB = b.purchase_price || 0
                return priceB - priceA
              }
              case 'cashflow': {
                try {
                  const cashFlowA = calculateCashFlow(a)
                  const cashFlowB = calculateCashFlow(b)

                  // Validate the results are finite numbers
                  const validCashFlowA = (typeof cashFlowA === 'number' && isFinite(cashFlowA)) ? cashFlowA : 0
                  const validCashFlowB = (typeof cashFlowB === 'number' && isFinite(cashFlowB)) ? cashFlowB : 0

                  // Sort in descending order (highest cash flow first)
                  return validCashFlowB - validCashFlowA
                } catch (cashFlowError) {
                  console.error('Cash flow calculation error during sorting:', cashFlowError, { propertyA: a.property_id, propertyB: b.property_id })
                  return 0
                }
              }
              case 'roi': {
                try {
                  const roiA = calculateROI(a)
                  const roiB = calculateROI(b)

                  // Validate the results are finite numbers
                  const validRoiA = (typeof roiA === 'number' && isFinite(roiA)) ? roiA : 0
                  const validRoiB = (typeof roiB === 'number' && isFinite(roiB)) ? roiB : 0

                  // Sort in descending order (highest ROI first)
                  return validRoiB - validRoiA
                } catch (roiError) {
                  console.error('ROI calculation error during sorting:', roiError, { propertyA: a.property_id, propertyB: b.property_id })
                  return 0
                }
              }
              default:
                return 0
            }
          } catch (sortError) {
            console.error('Individual sort comparison error:', sortError, { propertyA: a.property_id, propertyB: b.property_id })
            return 0
          }
        })
      } catch (sortingError) {
        console.error('Critical sorting error - returning original array:', sortingError)
        setSortingError(`Sorting by ${sortBy} failed: ${sortingError instanceof Error ? sortingError.message : String(sortingError)}`)
        // Return original filtered array if sorting fails completely
        // This prevents the component from crashing
        filtered = [...filtered]
      }

      return filtered
    } catch (error) {
      console.error('Critical error in filteredAndSortedProperties:', error)
      setSortingError(`Failed to process properties: ${error instanceof Error ? error.message : String(error)}`)
      // Return original properties as fallback
      return properties
    }
  }, [properties, searchTerm, filterType, sortBy])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8">
            <PortfolioStats properties={[]} loading={true} />
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold mb-2">Error Loading Portfolio</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <Button onClick={loadProperties}>Try Again</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Your Portfolio</h2>
            <p className="text-gray-600 mt-1">
              Manage and track your real estate investments
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="secondary" 
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import Properties
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleExportPortfolio}
              className="flex items-center gap-2"
              disabled={properties.length === 0}
            >
              <Download className="w-4 h-4" />
              Export Portfolio
            </Button>
            {selectedProperties.length >= 2 && (
              <Button 
                variant="secondary" 
                onClick={handleCompareProperties}
                className="flex items-center gap-2"
              >
                Compare ({selectedProperties.length})
              </Button>
            )}
            <Link to="/analyze">
              <Button className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Property
              </Button>
            </Link>
          </div>
        </div>

        {/* Portfolio Stats */}
        <div className="mb-8">
          <PortfolioStats
            properties={properties}
            analytics={portfolioAnalytics || undefined}
            loading={loading}
          />
        </div>

        {/* Analysis Coverage Chart */}
        {coverageData && properties.length > 0 && (
          <div className="mb-8">
            <AnalysisCoverageChart
              coverage={coverageData}
              loading={analyticsLoading}
            />
          </div>
        )}

        {/* Sorting Error Display */}
        {sortingError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="text-yellow-600 text-sm">
                <strong>Sorting Warning:</strong> {sortingError}. Properties are displayed in their default order.
              </div>
              <button
                onClick={() => setSortingError(null)}
                className="ml-auto text-yellow-600 hover:text-yellow-800"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search and Filters */}
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search properties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="buy-hold">Buy & Hold</option>
                <option value="flip">Fix & Flip</option>
                <option value="brrr">BRRR</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="date">Sort by Date</option>
                <option value="price">Sort by Price</option>
                <option value="cashflow">Sort by Cash Flow</option>
                <option value="roi">Sort by ROI</option>
              </select>
            </div>

            {/* View Toggle */}
            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Properties List */}
        {loading ? (
          <div className={
            viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              : "space-y-4"
          }>
            {[...Array(6)].map((_, i) => (
              viewMode === 'grid' ? (
                <PropertyCardSkeleton key={i} />
              ) : (
                <CompactPropertyCardSkeleton key={i} />
              )
            ))}
          </div>
        ) : filteredAndSortedProperties.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No properties found</h3>
            <p className="text-gray-600 mb-6">
              {properties.length === 0 
                ? "Start building your portfolio by analyzing your first property."
                : "No properties match your current search criteria."
              }
            </p>
            {properties.length === 0 && (
              <Link to="/analyze">
                <Button className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Analyze Your First Property
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className={
            viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              : "space-y-4"
          }>
            {filteredAndSortedProperties.map((property) => (
              <PropertyCard
                key={property.property_id}
                property={property}
                onView={handleViewProperty}
                onEdit={handleEditProperty}
                onDelete={handleDeleteProperty}
                compactView={viewMode === 'list'}
                isSelected={selectedProperties.includes(property.property_id)}
                onToggleSelect={togglePropertySelection}
              />
            ))}
          </div>
        )}
      </main>

      {/* Property Comparison Modal */}
      {showComparison && (
        <PropertyComparison
          properties={selectedPropertiesData}
          onClose={() => setShowComparison(false)}
          onRemoveProperty={handleRemoveFromComparison}
        />
      )}

      {/* Property Import Modal */}
      <PropertyImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        existingProperties={properties}
        onImportComplete={handleImportComplete}
      />
    </div>
  )
}

